import { CaseData, CompositeKeys, SubTable } from 'app/types-shared';
import { columnName, tableName } from 'app/area/case/case-data-model';

/**
 * Entry log changes on case data in wire format. Used in read mode, where there is no draft in the store
 * and the change is saved straight to the case.
 */

const log = columnName[tableName.involvedPersonInside];

type Row = CaseData | SubTable;

export const entryChange = {
  changed: 'changed',
  alreadyInside: 'alreadyInside',
  alreadyEgressed: 'alreadyEgressed',
  notFound: 'notFound'
};

export type EntryChange = typeof entryChange[keyof typeof entryChange];

export type RecordedBy = { value: number | string; description?: string };

const fieldValue = (row: Row, field: string) => row?.fields?.[field]?.values?.[0]?.v;

const isOpen = (entry: SubTable) => !fieldValue(entry, log.egressTime);

const entriesOf = (person: Row): SubTable[] => person?.subTables?.[tableName.involvedPersonInConfinedSpace] || [];

/**
 * Finds the row a store path points to. The first element of the path is the case itself.
 */
const findRow = (caseData: CaseData, path: CompositeKeys): SubTable | undefined =>
  path.slice(1).reduce<Row | undefined>((parent, element) => {
    const [table] = Object.keys(element);
    return parent?.subTables?.[table]?.find(row => row.primaryKey?.v === element[table]);
  }, caseData) as SubTable | undefined;

const recordedByValues = (recordedBy?: RecordedBy) =>
  recordedBy ? [{ v: recordedBy.value, d: recordedBy.description }] : [];

/**
 * Store path of the entry the involved person at `involvedPersonPath` is still inside of, if any.
 */
export const findOpenEntryPath = (caseData: CaseData, involvedPersonPath: CompositeKeys): CompositeKeys | undefined => {
  const openEntry = entriesOf(findRow(caseData, involvedPersonPath)).find(isOpen);
  return openEntry && [...involvedPersonPath, { [tableName.involvedPersonInConfinedSpace]: openEntry.primaryKey?.v }];
};

/**
 * Adds a new entry to the involved person at `involvedPersonPath`, unless the person is already inside.
 * Mutates `caseData`.
 */
export const addEntry = (
  caseData: CaseData,
  involvedPersonPath: CompositeKeys,
  entry: { entryTime: string; recordedBy?: RecordedBy }
): EntryChange => {
  const person = findRow(caseData, involvedPersonPath);
  if (!person) {
    return entryChange.notFound;
  }

  const entries = entriesOf(person);
  if (entries.some(isOpen)) {
    return entryChange.alreadyInside;
  }

  const nextId = entries.reduce((max, e) => Math.max(max, Number(e.primaryKey?.v) || 0), 0) + 1;
  const newEntry: SubTable = {
    primaryKey: { v: nextId },
    fields: {
      [log.entryTime]: { values: [{ v: entry.entryTime }] },
      ...(entry.recordedBy && { [log.entryTimeRecordedBy]: { values: recordedByValues(entry.recordedBy) } })
    }
  };

  person.subTables = {
    ...(person.subTables || {}),
    [tableName.involvedPersonInConfinedSpace]: [...entries, newEntry]
  };
  return entryChange.changed;
};

/**
 * Sets egress time and who recorded it on the entry at `entryPath`, unless it has already been egressed.
 * Mutates `caseData`.
 */
export const egressEntry = (
  caseData: CaseData,
  entryPath: CompositeKeys,
  egress: { egressTime: string; recordedBy?: RecordedBy }
): EntryChange => {
  const entry = findRow(caseData, entryPath);
  if (!entry) {
    return entryChange.notFound;
  }
  if (!isOpen(entry)) {
    return entryChange.alreadyEgressed;
  }

  entry.fields = {
    ...entry.fields,
    [log.egressTime]: { values: [{ v: egress.egressTime }] },
    [log.egressTimeRecordedBy]: { values: recordedByValues(egress.recordedBy) }
  };
  return entryChange.changed;
};
